import { i18n, LANGUAGE_LABELS } from '@atlas-frontend/i18n';

/**
 * Namespace under which the backend serves the atlas-airflow error message catalog
 * (`/api/translations`, merged by `useTranslationOverrides`). The keys are error codes,
 * the values i18next templates with `{{argument}}` placeholders.
 */
export const PROCESSING_ERRORS_NAMESPACE = 'processing-errors';

export type ProcessingErrorArgs = Record<string, unknown>;

export interface TranslatableProcessingError {
  errorCode?: string | null;
  errorArgs?: ProcessingErrorArgs | null;
  description: string;
}

/**
 * Message of an error code from the catalog in the current language, with the
 * arguments filled in. `undefined` when the catalog does not know the code, so the
 * caller can fall back to its own translations or to the stored text.
 */
export const translateProcessingErrorCode = (
  code: string | null | undefined,
  args?: ProcessingErrorArgs | string[] | null,
): string | undefined => {
  if (!code || !i18n.isInitialized) {
    return undefined;
  }
  const options = { ns: PROCESSING_ERRORS_NAMESPACE };
  if (i18n.language === LANGUAGE_LABELS) {
    return `${PROCESSING_ERRORS_NAMESPACE}:${code}`;
  }
  const translatedCode = findErrorCodeTranslation(code, (candidate) =>
    i18n.exists(candidate, options),
  );
  if (!translatedCode) {
    return undefined;
  }
  let messageArgs = args ?? {};
  if (args) {
    if (Array.isArray(args)) {
      messageArgs = {};
    } else {
      messageArgs = stringifyNested(args);
    }
  }
  return i18n.t(translatedCode, {
    ...options,
    ...messageArgs,
    // React escapes the rendered text itself; keep quotes and apostrophes in arguments as they are
    interpolation: { escapeValue: false },
  });
};

/**
 * Text to show for a processing error row: translated from its code and arguments when
 * the row carries them (rows written before the catalog existed have no arguments) and
 * the catalog knows the code, otherwise the description stored with the row. A row with
 * neither still has to say something, so it gets the catalog message without arguments
 * when the code is known and the bare error code when it is not. The backend applies the
 * same order to the Excel reports (`ProcessingErrorMessageService.describe`).
 */
export const translateProcessingError = (
  error: TranslatableProcessingError,
): string =>
  (error.errorArgs
    ? translateProcessingErrorCode(error.errorCode, error.errorArgs)
    : undefined) ??
  (error.description?.trim() ? error.description : undefined) ??
  translateProcessingErrorCode(error.errorCode) ??
  error.errorCode ??
  '';

type ExistsFunction = (key: string) => boolean;
type TranslateFunction = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/**
 * Translator for API error responses (`{ code, meta }`), for `useMutationErrors` and
 * friends. A component's own `alert.error.code.<code>` translation wins, because it may
 * carry context of its own (a correlation id, the action that failed); otherwise the
 * code is looked up in the processing error catalog; otherwise i18next shows the key.
 */
export const createApiErrorTranslator =
  ({ t, exists }: { t: TranslateFunction; exists: ExistsFunction }) =>
  (code: string, meta?: { args?: ProcessingErrorArgs | string[] }): string => {
    const key = `alert.error.code.${code}`;
    const options = meta?.args && { ...meta.args };
    const translatedCode = findErrorCodeTranslation(code, (candidate) =>
      exists(`alert.error.code.${candidate}`),
    );
    if (translatedCode) {
      return t(`alert.error.code.${translatedCode}`, options);
    }
    return translateProcessingErrorCode(code, meta?.args) ?? t(key, options);
  };

function findErrorCodeTranslation(code: string, exists: ExistsFunction) {
  return [code, code.toLowerCase(), code.toUpperCase()].find(exists);
}

type Stringified<T> = {
  [K in keyof T]: T[K] extends Function
    ? T[K]
    : T[K] extends object
      ? string
      : T[K];
};

function stringifyNested<T extends Record<string, unknown>>(
  obj: T,
): Stringified<T> {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k,
      v !== null && typeof v === 'object' ? JSON.stringify(v) : v,
    ]),
  ) as Stringified<T>;
}
